/**
 * Custom behavior for the redesigned announcement bar.
 *
 * This file is intentionally self-contained and does NOT modify or depend on any internals of theme.js. It only
 * uses standard browser APIs and Shopify's public /cart.js and /cart/update.js AJAX endpoints, plus the generic
 * "cart:change"/"cart:refresh" events that the theme's own cart components already dispatch/listen to. This means
 * a future theme update can safely overwrite theme.js/theme.css without ever touching or breaking this file.
 *
 * It reuses the theme's existing <countdown-timer-flip> element (unchanged, defined in theme.js) for the flip-digit
 * visuals, but defines its own <announcement-countdown-timer> wrapper instead of touching the theme's own
 * <countdown-timer> element, since a custom element tag can only be registered once.
 */

class AnnouncementCountdownTimer extends HTMLElement {
  connectedCallback() {
    this._flips = Array.from(this.querySelectorAll('countdown-timer-flip'));
    this._hasRestarted = false;
    this._isVisible = true;

    const expiresAt = this.getAttribute('expires-at');
    if (expiresAt) {
      this._expirationDate = new Date(expiresAt);
      this._interval = setInterval(() => this.recalculate(), 1000);
      this.recalculate();
    }

    this._visibilityObserver = new IntersectionObserver(
      (entries) => {
        this._isVisible = entries.some((entry) => entry.isIntersecting);
      },
      { rootMargin: '500px' }
    );
    this._visibilityObserver.observe(this);
  }

  disconnectedCallback() {
    clearInterval(this._interval);
    this._visibilityObserver?.disconnect();
  }

  recalculate() {
    const dateNow = new Date();

    if (this._expirationDate < dateNow) {
      const behavior = this.getAttribute('expiration-behavior');

      if (behavior === 'restart' && !this._hasRestarted) {
        this._hasRestarted = true;
        const restartAt = this.getAttribute('restart-at');
        const restartDate = restartAt ? new Date(restartAt) : null;

        if (restartDate && restartDate > dateNow) {
          this._expirationDate = restartDate;
          return this.recalculate();
        }

        return clearInterval(this._interval);
      }

      if (behavior === 'hide') {
        this.closest('.announcement-bar__block')?.remove();
      }

      return clearInterval(this._interval);
    }

    if (!this._isVisible) return;

    let delta = Math.abs(this._expirationDate - dateNow) / 1000;
    const days = Math.floor(delta / 86400);
    delta -= days * 86400;
    const hours = Math.floor(delta / 3600) % 24;
    delta -= hours * 3600;
    const minutes = Math.floor(delta / 60) % 60;
    delta -= minutes * 60;
    const seconds = Math.floor(delta % 60);

    this._flips.find((flip) => flip.getAttribute('type') === 'days')?.updateValue(days);
    this._flips.find((flip) => flip.getAttribute('type') === 'hours')?.updateValue(hours);
    this._flips.find((flip) => flip.getAttribute('type') === 'minutes')?.updateValue(minutes);
    this._flips.find((flip) => flip.getAttribute('type') === 'seconds')?.updateValue(seconds);
  }
}

if (!customElements.get('announcement-countdown-timer')) {
  customElements.define('announcement-countdown-timer', AnnouncementCountdownTimer);
}

class DiscountApplyButton extends HTMLElement {
  connectedCallback() {
    this._onClick = this.onClick.bind(this);
    this.addEventListener('click', this._onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._onClick);
  }

  async onClick(event) {
    const button = event.target.closest('button, a');
    if (!button) return;

    event.preventDefault();

    const code = this.getAttribute('discount-code');
    if (!code) return;

    button.setAttribute('aria-busy', 'true');

    try {
      const cartResponse = await fetch(`${window.Shopify.routes.root}cart.js`);
      const cart = await cartResponse.json();
      const existingCodes = (cart.discount_codes || [])
        .filter((entry) => entry.applicable)
        .map((entry) => entry.code)
        .filter((existingCode) => existingCode.toLowerCase() !== code.toLowerCase());
      const discount = existingCodes.concat(code).join(',');

      const response = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ discount })
      });
      const responseJson = await response.json();
      const applicable =
        response.ok &&
        (responseJson.discount_codes || []).some(
          (entry) => entry.code?.toLowerCase() === code.toLowerCase() && entry.applicable
        );

      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      this.showFeedback(applicable);

      if (applicable) {
        const redirect = this.getAttribute('redirect');
        if (redirect) window.location.href = redirect;
      }
    } catch {
      this.showFeedback(false);
    } finally {
      button.setAttribute('aria-busy', 'false');
    }
  }

  showFeedback(success) {
    const message = this.getAttribute(success ? 'success-message' : 'error-message');
    if (!message) return;

    let feedback = this.querySelector('.announcement-bar__discount-feedback');
    if (!feedback) {
      feedback = document.createElement('span');
      feedback.className = 'announcement-bar__discount-feedback';
      this.append(feedback);
    }

    feedback.textContent = message;
    feedback.classList.toggle('text-error', !success);
    feedback.hidden = false;
    clearTimeout(this._feedbackTimeout);
    this._feedbackTimeout = setTimeout(() => {
      feedback.hidden = true;
    }, 4000);
  }
}

if (!customElements.get('discount-apply-button')) {
  customElements.define('discount-apply-button', DiscountApplyButton);
}

// A thin fill bar that visualizes progress towards the free shipping threshold. Kept as its own element (rather
// than extending the native <free-shipping-bar>, which only ever updates its text) so it can react to the same
// "cart:change" event independently, without touching theme.js.
class ShippingProgressBar extends HTMLElement {
  connectedCallback() {
    this._onCartChanged = this.onCartChanged.bind(this);
    document.addEventListener('cart:change', this._onCartChanged);
    this.update(parseFloat(this.getAttribute('total-price')) || 0);
  }

  disconnectedCallback() {
    document.removeEventListener('cart:change', this._onCartChanged);
  }

  get threshold() {
    return parseFloat(this.getAttribute('threshold').replace(/[^0-9.]/g, '')) * 100;
  }

  onCartChanged(event) {
    const priceForItems = event.detail['cart']['items']
      .filter((item) => item['requires_shipping'])
      .reduce((sum, item) => sum + item['final_line_price'], 0);
    const cartDiscount = event.detail['cart']['cart_level_discount_applications'].reduce(
      (sum, discountAllocation) => sum + discountAllocation['discount_application']['total_allocated_amount'],
      0
    );
    this.update(priceForItems - cartDiscount);
  }

  update(totalPrice) {
    const percent = Math.min(100, Math.max(0, (totalPrice / this.threshold) * 100));
    // A visible minimum fill (below 100%) keeps the fill-end marker on-screen even on an empty cart, so the bar
    // reads as a progress gauge from the very first paint instead of looking like a plain, unfilled divider.
    const displayPercent = percent >= 100 ? 100 : Math.max(4, percent);
    const fill = this.querySelector('.announcement-bar__progress-fill');
    if (fill) fill.style.width = `${displayPercent}%`;
    this.classList.toggle('is-complete', percent >= 100);
  }
}

if (!customElements.get('shipping-progress-bar')) {
  customElements.define('shipping-progress-bar', ShippingProgressBar);
}

class StickyAnnouncementBar extends HTMLElement {
  connectedCallback() {
    this._scrollYTrackingPosition = Math.max(0, window.scrollY);
    this._isVisible = true;
    this._abortController = new AbortController();
    const { signal } = this._abortController;

    const closeButton = this.querySelector('[data-announcement-bar-close]');
    if (closeButton && this.hasAttribute('dismiss-key')) {
      if (this.isDismissed()) {
        this.hidden = true;
      } else {
        closeButton.addEventListener('click', () => this.dismiss(), { signal });
      }
    }

    if (this.hasAttribute('hide-on-scroll')) {
      window.addEventListener('scroll', () => this.detectScrollDirection(), { signal, passive: true });
    }
  }

  disconnectedCallback() {
    this._abortController?.abort();
  }

  // 0 remembered days means "only for the current browsing session": sessionStorage clears once the tab/browser
  // closes, unlike localStorage which would otherwise persist the dismissal forever.
  get dismissStorage() {
    const days = Number(this.getAttribute('remember-days') || 0);
    return days > 0 ? localStorage : sessionStorage;
  }

  isDismissed() {
    try {
      const raw = this.dismissStorage.getItem(this.getAttribute('dismiss-key'));
      if (!raw) return false;
      return raw === 'forever' || Number(raw) > Date.now();
    } catch {
      return false;
    }
  }

  dismiss() {
    const days = Number(this.getAttribute('remember-days') || 0);
    try {
      const key = this.getAttribute('dismiss-key');
      this.dismissStorage.setItem(key, days > 0 ? String(Date.now() + days * 86400000) : 'forever');
    } catch {
      // Ignore storage errors (e.g. private browsing mode).
    }
    this.hidden = true;
    this.dispatchEvent(new CustomEvent('announcement-bar:dismissed', { bubbles: true }));
  }

  detectScrollDirection() {
    const scrollY = Math.max(0, window.scrollY);
    if (scrollY > this._scrollYTrackingPosition && scrollY - this._scrollYTrackingPosition > 50) {
      this.setVisibility(false);
      this._scrollYTrackingPosition = scrollY;
    } else if (scrollY < this._scrollYTrackingPosition) {
      this.setVisibility(true);
      this._scrollYTrackingPosition = scrollY;
    }
  }

  setVisibility(isVisible) {
    if (isVisible !== this._isVisible) {
      this._isVisible = isVisible;
      this.classList.toggle('is-hidden', !isVisible);
    }
  }
}

if (!customElements.get('sticky-announcement-bar')) {
  customElements.define('sticky-announcement-bar', StickyAnnouncementBar);
}
